import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import appSettings from '../../../config/settings';
import { FormattedDate, FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  getStartOf,
  getStartOfWeek,
  isInRange,
  parseDateFromISO8601,
  parseDateTimeString,
  stringifyDateToISO8601,
  getStartOfMonth,
  getEndOfMonth,
  getDefaultTimeZoneOnBrowser,
} from '../../../util/dates';
import { availabilityPerDate } from '../../../util/generators';
import { createResourceLocatorString } from '../../../util/routes';
import { DATE_TYPE_DATE, DATE_TYPE_TIME, propTypes } from '../../../util/types';

// Import shared components
import {
  Heading,
  H4,
  IconArrowHead,
  IconDelete,
  IconSpinner,
  TimeRange,
  Modal,
} from '../../../components';

import {
  endOfAvailabilityExceptionRange,
  getStartOfNextMonth,
  getStartOfPrevMonth,
  handleMonthClick,
} from '../../EditListingPage/EditListingWizard/EditListingAvailabilityPanel/availability.helpers';

import Next from '../../EditListingPage/EditListingWizard/EditListingAvailabilityPanel/NextArrow';
import Prev from '../../EditListingPage/EditListingWizard/EditListingAvailabilityPanel/PrevArrow';
import css from './MonthlyCalendar.module.css';
import { DAY, isBookingProcessAlias, isFullDay } from '../../../transactions/transaction';
import { useConfiguration } from '../../../context/configurationContext';
import { formatMoney } from '../../../util/currency';

const TODAY = new Date();

/////////////
// Weekday //
/////////////

const parseLocalizedTime = (date, timeString, timeZone) => {
  const dateString = stringifyDateToISO8601(date, timeZone);
  return parseDateTimeString(`${dateString} ${timeString}`, timeZone);
};

const getEndTimeAsDate = (date, endTime, isDaily, timeZone) => {
  const endTimeAsDate =
    endTime == '00:00' && !isDaily
      ? getStartOf(date, 'day', timeZone, 1, 'days')
      : parseLocalizedTime(date, endTime, timeZone);
  return endTimeAsDate;
};

// Component that renders an entry in the availability plan (weekly schedule)
const PlanEntry = ({ date, entry, useFullDays, isDaily, timeZone, intl, ...rest }) => {
  const isAvailable = entry.seats > 0;
  const availabilityInfo = isAvailable ? (
    <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.available" values={{ seats: entry.seats }} />
  ) : (
    <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.notAvailable" />
  );

  return (
    <div className={css.planEntry} {...rest}>
      <div
        className={classNames(css.availabilityDot, {
          [css.isAvailable]: entry.seats > 0,
        })}
      />
      {useFullDays ? (
        availabilityInfo
      ) : (
        <TimeRange
          className={css.timeRange}
          startDate={parseLocalizedTime(date, entry.startTime, timeZone)}
          endDate={getEndTimeAsDate(date, entry.endTime, isDaily, timeZone)}
          seats={entry.seats}
          dateType={useFullDays ? DATE_TYPE_DATE : DATE_TYPE_TIME}
          timeZone={timeZone}
        />
      )}
    </div>
  );
};

// Component that renders an exception that touches the given date in the MonthlyCalendar
const ExceptionEntry = ({
  exception,
  useFullDays,
  isDaily,
  timeZone,
}) => {
  const millisecondBeforeEndTime = new Date(exception.attributes.end.getTime() - 1);
  const rangeEnd = isDaily ? millisecondBeforeEndTime : exception.attributes.end;
  return (
    <div className={css.exception}>
      <div
        className={classNames(css.availabilityDot, {
          [css.isAvailable]: exception.attributes.seats > 0,
        })}
      />
      <TimeRange
        className={css.timeRange}
        startDate={exception.attributes.start}
        endDate={rangeEnd}
        dateType={useFullDays ? DATE_TYPE_DATE : DATE_TYPE_TIME}
        timeZone={timeZone}
      />
    </div>
  );
};

// Component that renders all the ExceptionEntry components that allow availability (seats > 0)
const AvailableExceptionsInfo = ({
  availableExceptions,
  useFullDays,
  isDaily,
  timeZone,
}) => {
  const hasAvailableExceptions = availableExceptions.length > 0;
  const seats = hasAvailableExceptions ? availableExceptions[0].attributes.seats : null;
  return hasAvailableExceptions ? (
    <>
      <Heading as="h6" rootClassName={css.exceptionsSubtitle}>
        <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.available" values={{ seats }} />
      </Heading>
      {availableExceptions.map(exception => {
        return (
          <ExceptionEntry
            key={exception.id.uuid}
            exception={exception}
            timeZone={timeZone}
            useFullDays={useFullDays}
            isDaily={isDaily}
          />
        );
      })}
    </>
  ) : null;
};

// Component that renders all the ExceptionEntry components that blocks availability (seats === 0)
const NotAvailableExceptionsInfo = ({
  blockingExceptions,
  useFullDays,
  isDaily,
  timeZone,
}) => {
  const hasBlockingExceptions = blockingExceptions.length > 0;
  return hasBlockingExceptions ? (
    <>
      <Heading as="h6" rootClassName={css.exceptionsSubtitle}>
        <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.notAvailable" />
      </Heading>
      {blockingExceptions.map(exception => {
        return (
          <ExceptionEntry
            key={exception.id.uuid}
            exception={exception}
            timeZone={timeZone}
            useFullDays={useFullDays}
            isDaily={isDaily}
          />
        );
      })}
    </>
  ) : null;
};

const getModalDate = (params) => {
  let { date } = params || {};
  if (date) {
    const formattedDate = moment(date).format('MMMM D, YYYY');
    return <Heading as="h4">
      <FormattedMessage id="ProfilePage.MonthlyCalendar.modalDetailDate" values={{formattedDate}} />
    </Heading>;
  }

  return null;
};

const getFullEntries = params => {
  let { availabilityData, timeZone, isDaily, useFullDays, intl } = params;
  const hasPlanEntries = availabilityData?.planEntries?.length > 0;
  const date = parseDateFromISO8601(availabilityData?.id, timeZone);

  return hasPlanEntries ? (
    <div className={css.planEntries}>
      {availabilityData.planEntries.map((e, i) => {
        return (
          <PlanEntry
            key={`entry${i}`}
            date={date}
            entry={e}
            timeZone={timeZone}
            isDaily={isDaily}
            useFullDays={useFullDays}
            intl={intl}
          />
        );
      })}
    </div>
  ) : null
};

const getExceptions = params => {
  let {
    availabilityData,
    fetchExceptionsInProgress,
    fetchExceptionsError,
    useFullDays,
    isDaily,
    timeZone,
  } = params;

  const availableExceptions = availabilityData.exceptions.filter(e => e.attributes.seats > 0);
  const hasExceptions = availabilityData?.exceptions?.length > 0;
  const blockingExceptions = availabilityData.exceptions.filter(e => e.attributes.seats === 0);

  return hasExceptions || fetchExceptionsError ? (
    <div className={css.exceptionsContainer}>
      <Heading
        as="h5"
        rootClassName={css.exceptionsTitle}
        title="Exceptions overwrite weekly schedule"
      >
        <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.exceptions" />
      </Heading>
      {fetchExceptionsInProgress ? (
        <IconSpinner />
      ) : fetchExceptionsError ? (
        <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.fetchExceptionsError" />
      ) : (
        <>
          <AvailableExceptionsInfo
            availableExceptions={availableExceptions}
            useFullDays={useFullDays}
            isDaily={isDaily}
            timeZone={timeZone}
          />
          <NotAvailableExceptionsInfo
            blockingExceptions={blockingExceptions}
            useFullDays={useFullDays}
            isDaily={isDaily}
            timeZone={timeZone}
          />
        </>
      )}
    </div>
  ) : null;
};

const getListingData = ({currentListings = [], intl, config, date}) => {
  const listingsContainer = <div>
    {currentListings.map(listing => {
      let { price, title, publicData, availabilityPlan } = listing?.attributes || {};
      let formattedPrice;
      if (price && price.currency === config.currency) {
        formattedPrice = formatMoney(intl, price);
      }

      const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

      const unitType = publicData?.unitType;
      const useFullDays = isFullDay(unitType);

      let entriesParams = {
        availabilityData: {
          planEntries: listing?.planEntries || [],
          exceptions: listing?.exceptions || [],
          id: date,
        },
        timeZone: availabilityPlan?.timeZone,
        isDaily: unitType === DAY,
        useFullDays,
        intl
      };

      return <div className={css.listingContainer} key={listing.id.uuid}>
        <div className={css.listingTitle}>
          <div className={css.title}>{title || 'Listing'}</div>
          {formattedPrice ? <div className={css.price}>
            <div className={css.priceValue} title={formattedPrice}>
              {formattedPrice}
            </div>
            {isBookable ? (
              <div className={css.perUnit}>
                <FormattedMessage id="ListingCard.perUnit" values={{ unitType }} />
              </div>
            ) : null}
          </div> : null}
        </div>
        {getFullEntries(entriesParams)}
        {getExceptions(entriesParams)}
      </div>;
    })}
  </div>;

  return listingsContainer;
};

// The calendar date info (related to <DateLabel>)
const CalendarDate = props => {
  const intl = useIntl();
  const {
    availabilityData,
    hasAvailability,
    isDaily,
    useFullDays,
    onDeleteAvailabilityException,
    fetchExceptionsInProgress,
    fetchExceptionsError,
    timeZone,
  } = props;
  const hasPlanEntries = availabilityData?.planEntries?.length > 0;
  const hasExceptions = availabilityData?.exceptions?.length > 0;
  const availableExceptions = availabilityData.exceptions.filter(e => e.attributes.seats > 0);
  const blockingExceptions = availabilityData.exceptions.filter(e => e.attributes.seats === 0);
  const date = parseDateFromISO8601(availabilityData?.id, timeZone);

  let showMore = hasPlanEntries && availabilityData?.planEntries && Array.isArray(availabilityData.planEntries) && availabilityData.planEntries.length > 1;

  return (
    <div className={classNames(css.date, { [css.blockedDate]: !hasAvailability })}>
      <div className={css.info}>
        {hasPlanEntries ? <PlanEntry
            key={`entry0`}
            date={date}
            entry={availabilityData.planEntries[0]}
            timeZone={timeZone}
            isDaily={isDaily}
            useFullDays={useFullDays}
            intl={intl}
          /> : null}
        {showMore ? <div className={css.timeRange}>+{availabilityData.planEntries.length -1}</div> : null}
        {hasExceptions || fetchExceptionsError ? (
          <div className={css.monthExceptionsContainer}>
            {fetchExceptionsInProgress ? (
              <IconSpinner />
            ) : fetchExceptionsError ? (
              <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.fetchExceptionsError" />
            ) : (
              <>
                <div>Exceptions</div>
                {availableExceptions && availableExceptions.length ? <div>{availableExceptions.length} Available</div> : null}
                {blockingExceptions && blockingExceptions.length ? <div>{blockingExceptions.length} Not Available</div> : null}
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/////////////////////
// MonthlyCalendar //
/////////////////////

const getStartOfSelectedWeek = ({ locationSearch, timeZone, firstDayOfWeek }) => {
  const selectedDate = locationSearch?.d
    ? parseDateFromISO8601(locationSearch.d, timeZone)
    : new Date();
  return getStartOfWeek(selectedDate, timeZone, firstDayOfWeek);
};

const getStartOfSelectedMonth = ({ locationSearch, timeZone, firstDayOfWeek }) => {
  const selectedDate = locationSearch?.d
    ? parseDateFromISO8601(locationSearch.d, timeZone)
    : new Date();
  return getStartOfMonth(selectedDate, timeZone, firstDayOfWeek);
};

// MonthlyCalendar shows the weekly data (plan entries & exceptions) on selected week (on given time zone)
const MonthlyCalendar = props => {
  const config = useConfiguration();
  const defaultTimeZone = () =>
    typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

  const defaultAvailabilityPlan = {
    type: 'availability-plan/time',
    timezone: defaultTimeZone(),
    entries: [
      // { dayOfWeek: 'mon', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'tue', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'wed', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'thu', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'fri', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sat', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sun', startTime: '09:00', endTime: '17:00', seats: 1 },
    ],
  };

  const timeZone = props.listings[0]?.attributes?.availabilityPlan?.timezone || defaultTimeZone();

  const [currentMonth, setCurrentMonth] = useState(
    getStartOfSelectedMonth({ ...props, timeZone })
  );
  const intl = useIntl();

  const {
    className,
    rootClassName,
    headerClassName,
    listingId,
    availabilityPlan,
    availabilityExceptions,
    weeklyExceptionQueries,
    monthlyExceptionQueries,
    isDaily,
    useFullDays,
    onDeleteAvailabilityException,
    onFetchExceptions,
    params,
    firstDayOfWeek,
    routeConfiguration,
    history,
    listings,
    exceptions,
  } = props;
  // Get all the AvailabilityExcetions
  const thisMonth = getStartOfMonth(TODAY, timeZone, firstDayOfWeek);
  const nextMonth = getStartOf(currentMonth, 'day', timeZone, 1, 'months');

  let availableDatesArray = listings.map(l => {
    const availabilityPlan = l?.attributes?.availabilityPlan || defaultAvailabilityPlan;
    l.availableDates = availabilityPerDate(
        currentMonth,
        nextMonth,
        availabilityPlan,
        exceptions.filter(e => e?.attributes?.listingId == l?.id?.uuid)
      );
    return l;
  });

  let availableDatesObj = {};

  availableDatesArray.forEach(listing => {
    const { id, attributes, images, availableDates } = listing;
    let { title, description, price, publicData, availabilityPlan } = attributes;

    for (const date in availableDates) {
      const { planEntries, exceptions, hasAvailability, ranges } = availableDates[date];

      if (!availableDatesObj[date]) {
        availableDatesObj[date] = { id: availableDates[date].id, listings: [] };
      }

      if (planEntries.length > 0 || exceptions.length > 0) {
        availableDatesObj[date].listings.push({
          id,
          attributes: { title, description, price, publicData, availabilityPlan },
          images,
          planEntries, exceptions, hasAvailability, ranges,
        });
      }
    }
  });

  // const currenMonthId = stringifyDateToISO8601(currentMonth, timeZone);
  // const { fetchExceptionsInProgress, fetchExceptionsError } =
    // monthlyExceptionQueries[currenMonthId] || {};

  // useEffect(() => {
  //   // On development mode, print also range info if "verbose" flag is true.
  //   if (appSettings.dev && appSettings.verbose && fetchExceptionsInProgress === false) {
  //     const formatRange = (start, end, available, timeZone) => {
  //       const formattingOptions = { hour: 'numeric', minute: 'numeric', timeZone };
  //       const formattedStart = intl.formatDate(start, formattingOptions);
  //       const formattedEnd = intl.formatDate(end, formattingOptions);
  //       const availabilityEmoji = available ? '✅' : '🚫';
  //       console.log('          ', availabilityEmoji, formattedStart, ' - ', formattedEnd);
  //     };

  //     console.log('\n\n%cDaily availability ranges:', 'font-weight: bold;');
  //     Object.keys(availableDatesArray[0]?.availableDates).forEach(d => {
  //       const weekday = intl.formatDate(availableDatesArray[0]?.availableDates[d]?.ranges?.[0]?.start, { weekday: 'long' });
  //       console.log(`\n${d} ${weekday}`);
  //       availableDatesArray[0]?.availableDates[d].ranges.map(r => {
  //         formatRange(r.start, r.end, r.seats > 0, timeZone);
  //       });
  //     });
  //   }
  // }, [currentMonth, fetchExceptionsInProgress]);

  // const daysOfMonthStrings = Object.keys(availableDatesArray[0]?.availableDates || []);

  const monthClickParams = {
    currentMonth,
    setCurrentMonth,
    monthlyExceptionQueries,
    listings: listings.map(l => l.id),
    listingId,
    timeZone,
    onFetchExceptions,
    firstDayOfWeek,
    fullString: true
  };
  const onMonthClick = handleMonthClick(monthClickParams);

  const classes = classNames(rootClassName || css.root, className);

  const [days, setDays] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());

  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    generateCalendar();
  }, [month, year]);

  const generateCalendar = () => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const calendarDays = [];

    for (let i = 0; i < firstDay; i++) {
      calendarDays.push('');
    }

    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push(i);
    }

    // daysOfMonthStrings.forEach(d => {
      // calendarDays.push(d);
    // });

    setDays(calendarDays);
  };

  const date = new Date(year, month);
  const options = { month: 'long', year: 'numeric' };

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  return (
    <section className={classes}>
      <header className={css.sectionHeader}>
        <div className={css.titleWrapper}>
          <Heading as="h2" className={css.sectionTitle}>
            <FormattedMessage id="EditListingAvailabilityPanel.MonthlyCalendar.scheduleTitle" />
          </Heading>
          {formattedDate}
        </div>
        <div className={css.navigation}>
          <Prev
            className={css.prev}
            onClick={() => {
              let prevMonth = month === 0 ? 11 : month - 1;
              let prevYear = month === 0 ? year - 1 : year;

              setMonth(prevMonth);
              setYear(prevYear);

              // setCurrentMonth(new Date(prevYear, prevMonth, 1));

              onMonthClick(getStartOfPrevMonth)
            }}
            showUntilDate={thisMonth}
            startOfPrevRange={getStartOfPrevMonth(currentMonth, timeZone)}
            size="big"
          />
          <Next
            className={css.next}
            onClick={() => {
              let nextMonth = month === 11 ? 0 : month + 1;
              let nextYear = month === 11 ? year + 1 : year;

              setMonth(nextMonth);
              setYear(nextYear);

              // setCurrentMonth(new Date(nextYear, nextMonth, 1));

              onMonthClick(getStartOfNextMonth)
            }}
            showUntilDate={endOfAvailabilityExceptionRange(timeZone, TODAY)}
            startOfNextRange={getStartOfNextMonth(currentMonth, timeZone)}
            size="big"
          />
        </div>
      </header>

      <Modal
        id="AuthenticationPage.tos"
        isOpen={modalData !== null}
        onClose={() => setModalData(null)}
        usePortal
        onManageDisableScrolling={props.onManageDisableScrolling}
      >
        {modalData ? <div>
          {getModalDate(modalData)}
          {getListingData(modalData)}
        </div> : null}
      </Modal>

      <div className={css.calendarGrid}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
          <div key={dayName} className={css.dayName}>
            {dayName}
          </div>
        ))}
        {days.map((day, index) => {
          let dayString;
          if (day !== '') {
            dayString = moment({ year, month, day }).format('YYYY-MM-DD');
          }
          let availabilityDataItem = dayString ? availableDatesObj[dayString] : {};
          let currentListings = (availabilityDataItem?.listings || []).filter(l => l.hasAvailability);

          let paramsForModal = null;
          if (currentListings.length) {
            paramsForModal = {
              currentListings,
              date: dayString,
              intl,
              config,
            };
          }

          return <div key={index} onClick={() => {
            if (!!paramsForModal) {
              setModalData(paramsForModal);
            }
          }} className={classNames(css.day, { [css.empty]: !day })}>
            {!!day ? <div className={css.dayNumber}>{day}</div> : null}
            {!!day ? <div className={classNames(css.date, { [css.blockedDate]: !currentListings.length })}>
              <div className={css.info}>{currentListings.length || ``}</div>
            </div> : null}
          </div>;
        })}
      </div>
    </section>
  );
};

MonthlyCalendar.defaultProps = {
  rootClassName: null,
  className: null,
  headerClassName: null,
  locationSearch: null,
  availabilityExceptions: [],
  firstDayOfWeek: 0,
};

MonthlyCalendar.propTypes = {
  rootClassName: string,
  className: string,
  headerClassName: string,
  locationSearch: shape({
    d: string,
  }),
  // listingId: propTypes.uuid.isRequired,
  // availabilityPlan: propTypes.availabilityPlan.isRequired,
  availabilityExceptions: arrayOf(propTypes.availabilityException),
  // weeklyExceptionQueries: object.isRequired,
  monthlyExceptionQueries: object,
  // isDaily: bool.isRequired,
  // useFullDays: bool.isRequired,
  // onDeleteAvailabilityException: func.isRequired,
  // onFetchExceptions: func.isRequired,
  // params: object.isRequired, // path params
  firstDayOfWeek: number,
  // routeConfiguration: arrayOf(propTypes.route).isRequired,
  // history: shape({
    // replace: func.isRequired,
  // }).isRequired,
};

export default MonthlyCalendar;
